import { HomeIcon, TagIcon } from 'components/icons/Icons';
import { Building2, Users } from 'lucide-react';
import React from 'react';
import { useLocation, Link, useParams } from 'react-router-dom';

// Interface pour définir une route du breadcrumb
interface BreadcrumbRoute {
  path: string;
  label: string | ((params: Record<string, string | undefined>) => string);
  icon?: React.ReactNode;
}

// Configuration des routes pour le breadcrumb
interface BreadcrumbConfig {
  [key: string]: BreadcrumbRoute;
}

// Props du composant Breadcrumb
interface BreadcrumbProps {
  config: BreadcrumbConfig;
  separator?: React.ReactNode;
  className?: string;
}

export const BoBreadcrumb: React.FC<BreadcrumbProps> = ({
  config,
  separator = '/',
  className = ''
}) => {
  const location = useLocation();
  const params = useParams();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Construire les éléments du breadcrumb
  const breadcrumbItems: Array<BreadcrumbRoute & { actualPath: string }> = [];

  // Toujours ajouter l'accueil
  if (config['/']) {
    breadcrumbItems.push({
      ...config['/'],
      actualPath: config['/'].path
    });
  }

  // Construire le chemin progressivement
  let currentPath = '';
  pathSegments.forEach((segment) => {
    currentPath += `/${segment}`;

    // Vérifier si c'est un paramètre (UUID, ID, etc.)
    const isParam = /^[a-f0-9-]{36}$|^\d+$/.test(segment);

    if (isParam) {
      // Chercher une route avec paramètre dans la config
      const paramRoute = Object.keys(config).find(key => {
        const pattern = key.replace(/:\w+/g, '[^/]+');
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(currentPath);
      });

      if (paramRoute) {
        const route = config[paramRoute];
        const enrichedParams = { ...params, id: segment };
        breadcrumbItems.push({
          ...route,
          actualPath: currentPath,
          label: typeof route.label === 'function'
            ? route.label(enrichedParams)
            : route.label
        });
      } else {
        // Afficher le paramètre tel quel si pas de config
        breadcrumbItems.push({
          path: currentPath,
          actualPath: currentPath,
          label: segment
        });
      }
    } else if (config[currentPath]) {
      breadcrumbItems.push({
        ...config[currentPath],
        actualPath: currentPath
      });
    }
  });

  if (breadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <nav className={`flex mb-6 items-center space-x-2 text-sm text-gray-600 ${className}`}>
      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1;
        const label = typeof item.label === 'function' ? item.label(params) : item.label;

        return (
          <React.Fragment key={item.actualPath}>
            {index > 0 && (
              <span className="text-zinc-400 mx-2">{separator}</span>
            )}
            {isLast ? (
              <span className="text-zinc-900 font-medium flex items-center gap-1">
                {item.icon}
                {label}
              </span>
            ) : (
              <Link
                to={item.actualPath}
                className="hover:text-primary transition-colors flex items-center gap-1"
              >
                {item.icon}
                {label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export const boBreadcrumbConfig: BreadcrumbConfig = {
  '/': {
    path: '/back-office/documents',
    label: 'Accueil',
    icon: <HomeIcon className='w-4 h-4' />
  },
  '/back-office/documents': {
    path: '/back-office/documents',
    label: 'Documents',
    icon: <HomeIcon className='w-4 h-4' />
  },
  '/back-office/plateformes': {
    path: '/back-office/plateformes',
    label: 'Plateformes',
    icon: <Building2 className='w-4 h-4' />
  },
  '/back-office/plateformes/:plateformeId': {
    path: '/back-office/plateformes/:plateformeId',
    label: (params) => `Plateforme ${params.plateformeId || ''}`,
    icon: <Building2 className='w-4 h-4' />
  },
  '/back-office/acteurs/courtiers': {
    path: '/back-office/acteurs/courtiers',
    label: 'Courtiers',
    icon: <Users className='w-4 h-4' />
  },
  '/back-office/acteurs/courtiers/:brokerId': {
    path: '/back-office/acteurs/courtiers/:brokerId',
    label: (params) => `Courtier ${params.brokerId || ''}`,
    icon: <Users className='w-4 h-4' />
  },
  '/back-office/acteurs/clients': {
    path: '/back-office/acteurs/clients',
    label: 'Clients',
    icon: <Users className='w-4 h-4' />
  },
  '/back-office/produits': {
    path: '/back-office/produits',
    label: 'Produits',
    icon: <TagIcon className='w-4 h-4' />
  },
  '/back-office/produits/:productId': {
    path: '/back-office/produits/:productId',
    label: (params) => `Produit ${params.productId || ''}`,
    icon: <TagIcon className='w-4 h-4' />
  },
  '/back-office/contrats-groupe': {
    path: '/back-office/contrats-groupe',
    label: 'Contrats collectifs',
    icon: <TagIcon className='w-4 h-4' />
  },
  '/back-office/contrats-groupe/:groupContractId': {
    path: '/back-office/contrats-groupe/:groupContractId',
    label: (params) => `Contrat ${params.groupContractId || ''}`,
    icon: <TagIcon className='w-4 h-4' />
  },
  '/back-office/tunnels': {
    path: '/back-office/tunnels',
    label: 'Tunnels',
    icon: <TagIcon className='w-4 h-4' />
  }
};
